// Archived organizations that match what was typed, with a Restore button: organizations with no tests since 2020 are
// archived, and when one books again staff restore it instead of making a duplicate.
import { useState } from 'react';
import { canEditOrgs, post } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { orgMatches } from '../lib/orgSearch.ts';
import { useData } from '../lib/useData.ts';

interface Archived { id: string; name: string; participants: number; archivedReason: string | null }

/** With `query`, matches that text; without it, shows its own search box ("Can't find it?"). */
export function ArchivedMatches({ query, onRestored }: { query?: string; onRestored: (id: string, name: string) => void }) {
  const { me } = useAuth();
  const [own, setOwn] = useState('');
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [restored, setRestored] = useState<Set<string>>(new Set());
  const q = (query ?? own).trim();
  const list = useData<Archived[]>(q.length >= 3 ? '/api/departments?archived=1' : null);
  const hits = q.length >= 3 ? (list.data ?? []).filter((d) => !restored.has(d.id) && orgMatches(d.name, q)).slice(0, 6) : [];
  const restore = async (d: Archived) => {
    if (!window.confirm(`Restore "${d.name}"? It comes back on every organization list with its ${d.participants} people and history.`)) return;
    setErr(null);
    try {
      await post(`/api/departments/${d.id}/restore`);
      setRestored((cur) => new Set(cur).add(d.id));
      onRestored(d.id, d.name);
    } catch (e) { setErr((e as Error).message); }
  };
  const body = hits.length > 0 && (
    <div className="alert info small">
      <b>Archived {hits.length === 1 ? 'organization' : 'organizations'} matching “{q}”</b> (no tests since 2020):
      <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
        {hits.map((d) => (
          <li key={d.id}>{d.name} <span className="muted">({d.participants} people)</span>{' '}
            {me && canEditOrgs(me.role)
              ? <button type="button" className="secondary small" onClick={() => void restore(d)}>Restore</button>
              : <span className="muted">ask an admin to restore it</span>}
          </li>))}
      </ul>
      {err && <div className="error">{err}</div>}
    </div>);
  if (query !== undefined) return body || null;
  return (
    <div className="small">
      {!open
        ? <button type="button" className="secondary small" onClick={() => setOpen(true)}>Can't find it? Search archived organizations</button>
        : <><input placeholder="Search archived organizations…" value={own} onChange={(e) => setOwn(e.target.value)} autoFocus />
            {q.length >= 3 && !hits.length && list.data && <div className="muted">No archived organization matches.</div>}
            {body}</>}
    </div>);
}
